import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useChatContext } from '@/chat/ChatContext';
import { WIDE_SHELL_MIN_WIDTH } from '@/chat/useShellWidth';
import { ChipAwareMarkdown } from '@/markdown/chips';
import { getLabel } from '@/i18n/maps';
import { openInNewTab, renderUrlTemplate } from '@/utils/url-template';
import { detectProductType } from '@/utils/productType';
import { sizedImage } from '@/utils/images';
import { useDragScroll } from '@/utils/useDragScroll';
import { ChevronRightIcon, ShoppingCartIcon } from '@/components/icons';
import type { Product, ProductVariant } from '@/types/chat';
import type { Locale } from '@/types/widget';

export interface ProductCardProps {
  product: Product;
  /** Paragraph explaining this recommendation (set by attachExplanations). */
  explanation?: string;
}

/** Title + tagline share this many lines; the title is capped at 2 first. */
const HEAD_LINE_BUDGET = 3;
const NBSP = String.fromCharCode(0xa0);

/**
 * Layout lives in styles/index.css (.cal-pcard): no frame, a hairline above and
 * below. The top (photo + head) opens the preview. Under it, the purchase area —
 * wide shells: the formats as a row of tabs, then [chosen format's details][price]
 * [cart]; narrow ones: a full-width format field that unfolds the list, with
 * [price][Ajouter au panier] below it. Then the explanation (folded under
 * "Pourquoi ce produit" on narrow shells, open by default).
 */
export function ProductCard({ product, explanation }: ProductCardProps): JSX.Element {
  const { onAddToCart, onProductDetailsClick, productPageUrl, onPreviewProduct, shellWidth, locale } =
    useChatContext();
  const [variant, choose] = useChosenVariant(product);
  const image = variant?.image ? sizedImage(variant.image, variant.imageFormats, 'small') : product.image || '';
  const [adding, setAdding] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);
  const taglineLines = useTaglineLines(titleRef);
  // Same default as ProductJustification: wide until the first measurement.
  const narrow = shellWidth != null && shellWidth < WIDE_SHELL_MIN_WIDTH;

  async function handleAddToCart() {
    if (variant?.availability !== 'AVAILABLE' || adding) return;
    setAdding(true);
    try {
      await onAddToCart(variant.id, 1);
    } finally {
      setAdding(false);
    }
  }

  function handlePreview() {
    if (onPreviewProduct) {
      onPreviewProduct(product.slug);
    } else {
      handleViewDetails();
    }
  }

  function handleViewDetails() {
    if (onProductDetailsClick) {
      onProductDetailsClick(product.id, product.slug);
    } else if (productPageUrl) {
      openInNewTab(renderUrlTemplate(productPageUrl, { slug: product.slug }));
    } else {
      openInNewTab(product.url);
    }
  }

  const formatTag = shortFormat(product);

  return (
    <article className={narrow ? 'cal-pcard is-narrow' : 'cal-pcard'}>
      {/* The whole top (photo + head) opens the preview, like the old card. The title
          button is the keyboard/AT target; its click bubbles up to here. */}
      <div className="cal-pcard-top gtmtrack" content-content="chatbot-carte-ouvrir" onClick={handlePreview}>
        <div className="cal-pcard-photo">
          {image ? (
            // key: a new format's photo mounts fresh and fades in once loaded
            <img
              key={image}
              src={image}
              alt=""
              loading="lazy"
              onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
            />
          ) : (
            <span className="cal-pcard-photo-empty">Calebasse</span>
          )}
        </div>

        <div className="cal-pcard-head">
          <button type="button" aria-label={`Aperçu de ${product.title}`} className="cal-pcard-title">
            {/* " - " separators stay glued to the word before them: no line starts with a dash. */}
            <span ref={titleRef} className="cal-pcard-clamp">
              {product.title.replace(/ - /g, `${NBSP}- `)}
            </span>
          </button>
          {formatTag && <span className="cal-pcard-badge">{formatTag}</span>}
          {product.foreword && (
            <p className="cal-pcard-tagline cal-pcard-clamp" style={{ WebkitLineClamp: taglineLines }}>
              {product.foreword}
            </p>
          )}
        </div>
      </div>

      {/* No variant means this data came from an old session snapshot — price and
          stock aren't trustworthy, so we hide them rather than show a lying "0,00 €". */}
      {variant && (
        <Purchase
          variants={product.variants}
          variant={variant}
          onChoose={choose}
          onAdd={handleAddToCart}
          adding={adding}
          narrow={narrow}
        />
      )}

      {explanation && <Explanation body={explanation} narrow={narrow} locale={locale} />}
    </article>
  );
}

function Purchase({
  variants,
  variant,
  onChoose,
  onAdd,
  adding,
  narrow,
}: {
  variants: ProductVariant[];
  variant: ProductVariant;
  onChoose: (v: ProductVariant) => void;
  onAdd: () => void;
  adding: boolean;
  narrow: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const several = variants.length > 1;

  const lead = priceLead(variant);
  const final = finalPrice(variant);
  const price = (lead.length > 0 || final != null) && (
    <span className="cal-pcard-price">
      {lead.length > 0 && <span className="cal-pcard-was">{lead}</span>}
      {final != null && <span className="cal-pcard-now">{formatPrice(final)}</span>}
    </span>
  );
  const cart = (
    <button
      type="button"
      onClick={onAdd}
      disabled={variant.availability !== 'AVAILABLE' || adding}
      aria-label="Ajouter au panier"
      className="cal-pcard-cart gtmtrack"
      content-content="chatbot-carte-panier"
    >
      <ShoppingCartIcon width={17} height={17} />
      {narrow && <span>Ajouter au panier</span>}
    </button>
  );

  if (narrow) {
    return (
      <div className="cal-pcard-buy">
        <FormatField variant={variant} expanded={several ? open : undefined} onToggle={() => setOpen(!open)} />
        {open && (
          <FormatList
            variants={variants}
            variant={variant}
            onChoose={(v) => {
              onChoose(v);
              setOpen(false);
            }}
          />
        )}
        <div className="cal-pcard-buyrow">
          {price}
          {cart}
        </div>
      </div>
    );
  }
  return (
    <div className="cal-pcard-buy">
      {/* one row: the formats (their tabs, or the only one's name), then the price and the cart */}
      <div className="cal-pcard-buyrow">
        {several ? (
          <FormatTabs variants={variants} variant={variant} onChoose={onChoose} />
        ) : (
          <span className="cal-pcard-detail">{capitalize(variant.title)}</span>
        )}
        <span className="cal-pcard-buyend">
          {price}
          {cart}
        </span>
      </div>
      {/* the chosen format's details ("Gélule : 100 gélules…"), right under its tab — when it has some */}
      {several && formatDetail(variant.title) && <p className="cal-pcard-caption">{formatDetail(variant.title)}</p>}
    </div>
  );
}

/**
 * Wide card: the formats as short names ("Gélule", "Cure de 3 mois"), the chosen one
 * underlined. One line: when they don't fit, it scrolls sideways (a mouse drags it); the
 * chosen one is kept in view, also when it was chosen elsewhere (the side panel).
 */
function FormatTabs({
  variants,
  variant,
  onChoose,
}: {
  variants: ProductVariant[];
  variant: ProductVariant;
  onChoose: (v: ProductVariant) => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useDragScroll(() => 1); // tabs of all widths: no snapping, it stays where let go

  useLayoutEffect(() => {
    const strip = ref.current;
    const tab = strip?.querySelector('[aria-checked="true"]');
    if (!strip || !tab) return;
    const s = strip.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (t.left < s.left) strip.scrollLeft -= s.left - t.left;
    else if (t.right > s.right - TABS_FADE_PX) strip.scrollLeft += t.right - (s.right - TABS_FADE_PX);
  }, [variant]);

  return (
    <div ref={ref} role="radiogroup" aria-label="Format" className="cal-pcard-tabs" {...drag}>
      {variants.map((v, i) => (
        <button
          key={`${v.id}-${i}`}
          type="button"
          role="radio"
          aria-checked={v === variant}
          disabled={v.availability !== 'AVAILABLE'}
          onClick={() => onChoose(v)}
          className="cal-pcard-tab gtmtrack"
          content-content="chatbot-carte-format"
        >
          {formatName(v.title)}
          {discountTag(v)}
        </button>
      ))}
    </div>
  );
}

/**
 * The backend titles a format "Format Gélule : 100 gélules de poudre…": the part
 * before " : " (minus "Format") names it, the rest describes it. Older titles
 * ("1 flacon de 100 gélules - 50g") have no " : " — the whole title is the name.
 */
/** the fade at the tab strip's right edge (index.css .cal-pcard-tabs) */
const TABS_FADE_PX = 24;

export function formatName(title: string): string {
  const i = title.indexOf(' : ');
  return capitalize((i > 0 ? title.slice(0, i) : title).replace(/^format\s+/i, '').trim());
}

export function formatDetail(title: string): string {
  const i = title.indexOf(' : ');
  return i > 0 ? title.slice(i + 3).trim() : '';
}

/**
 * The format the reader chose for this product — shared with the preview pane and
 * any other card of the same product through ChatContext — else its default one.
 */
export function useChosenVariant(
  product: Product,
): [ProductVariant | undefined, (v: ProductVariant) => void] {
  const { chosenVariants, chooseVariant } = useChatContext();
  const [ownChoice, setOwnChoice] = useState<string | undefined>(); // outside ChatWidget
  const id = chosenVariants ? chosenVariants[product.slug] : ownChoice;
  const variant =
    product.variants.find((v) => id !== undefined && String(v.id) === id) ??
    product.variants.find((v) => v.isDefault) ??
    product.variants[0];
  const choose = (v: ProductVariant) =>
    chooseVariant ? chooseVariant(product.slug, String(v.id)) : setOwnChoice(String(v.id));
  return [variant, choose];
}

/**
 * Narrow card: "Format : <name> ▾" — the field that unfolds the list of formats.
 * With one format (expanded undefined) it is plain text, not a button. The label is
 * dropped when the title already starts with "Format".
 */
/**
 * Narrow card: "FORMAT" above, the chosen one on an underline, no box — "Gélule · 100 gélules…".
 * With several formats it opens their list under it, the line turning green.
 */
function FormatField({
  variant,
  expanded,
  onToggle,
}: {
  variant: ProductVariant;
  expanded?: boolean;
  onToggle: () => void;
}): JSX.Element {
  const detail = formatDetail(variant.title);
  const value = (
    <span className="cal-pcard-fmt-name">
      <b>{formatName(variant.title)}</b>
      {detail && ` · ${detail}`}
    </span>
  );
  return (
    <div className="cal-pcard-fmtfield">
      <span className="cal-pcard-fmt-label">Format</span>
      {expanded === undefined ? (
        <span className="cal-pcard-fmt is-static">{value}</span>
      ) : (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="cal-pcard-fmt gtmtrack"
          content-content="chatbot-carte-formats"
        >
          {value}
          <ChevronRightIcon width={16} height={16} />
        </button>
      )}
    </div>
  );
}

/**
 * Every format, a row each between hairlines: its name and details, its price; the chosen one
 * tinted. Out-of-stock ones are listed but can't be chosen.
 */
function FormatList({
  variants,
  variant,
  onChoose,
}: {
  variants: ProductVariant[];
  variant: ProductVariant;
  onChoose: (v: ProductVariant) => void;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label="Format" className="cal-pcard-fmtlist">
      {variants.map((v, i) => {
        const optPrice = finalPrice(v);
        return (
          <button
            key={`${v.id}-${i}`}
            type="button"
            role="radio"
            aria-checked={v === variant}
            disabled={v.availability !== 'AVAILABLE'}
            onClick={() => onChoose(v)}
            className="cal-pcard-opt gtmtrack"
            content-content="chatbot-carte-format"
          >
            <span className="cal-pcard-opt-dot" />
            <span className="cal-pcard-opt-name">
              {formatName(v.title)}
              {formatDetail(v.title) && <span className="cal-pcard-opt-sub">{formatDetail(v.title)}</span>}
            </span>
            <span className="cal-pcard-opt-price">
              {priceLead(v)}
              {optPrice != null && formatPrice(optPrice)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** What the customer pays: the backend's discountPrice when it sends one. */
export function finalPrice(v: ProductVariant): number | undefined {
  return v.discountPrice ?? v.price;
}

/**
 * Tags shown before a price: "Rupture", the discount ("-10 %") and the crossed-out
 * original. The original is only crossed out when the backend sends a lower
 * discountPrice — a bare "discount" can't be applied client-side (its price may
 * already include it).
 */
export function priceLead(v: ProductVariant): JSX.Element[] {
  const lead: JSX.Element[] = [];
  if (v.availability !== 'AVAILABLE') lead.push(<span key="oos" className="cal-pcard-oos">Rupture</span>);
  const off = discountTag(v);
  if (off) lead.push(off);
  if (v.price != null && v.discountPrice != null && v.discountPrice < v.price) {
    lead.push(<s key="was">{formatPrice(v.price)}</s>);
  }
  return lead;
}

/** The "-10 %" tag, when the backend marks the format as discounted. */
export function discountTag(v: ProductVariant): JSX.Element | null {
  const pct = parseFloat(String(v.discount ?? '').replace(',', '.'));
  if (!(pct > 0)) return null;
  return (
    <span key="off" className="cal-pcard-off">
      -{String(pct).replace('.', ',')}
      {NBSP}%
    </span>
  );
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function Explanation({ body, narrow, locale }: { body: string; narrow: boolean; locale: Locale }) {
  const box = (
    <div className="cal-pcard-explain">
      <span className="cal-pcard-explain-bar" />
      <ChipAwareMarkdown source={body} className="cal-pcard-explain-text" />
    </div>
  );
  if (!narrow) return box;
  return (
    <details className="cal-pcard-acc" open>
      <summary className="gtmtrack" content-content="chatbot-carte-pourquoi">
        {getLabel('product_about', locale)}
        <ChevronRightIcon width={16} height={16} />
      </summary>
      {box}
    </details>
  );
}

/**
 * Lines left for the tagline once the title (CSS-capped at 2) has rendered:
 * a 2-line title leaves 1, a 1-line title leaves 2. CSS can't count another
 * element's lines, so measure the title, again whenever it resizes (shell
 * width change, web font swap).
 */
function useTaglineLines(titleRef: RefObject<HTMLElement>): number {
  const [lines, setLines] = useState(HEAD_LINE_BUDGET - 1);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const titleLines = lineHeight ? Math.round(el.getBoundingClientRect().height / lineHeight) : 1;
      setLines(Math.max(1, HEAD_LINE_BUDGET - titleLines));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [titleRef]);
  return lines;
}

/**
 * Pick a compact format word for the inline chip: "Tisane", "Gélules",
 * "Bain" etc.
 *
 * The product's own title/families decide first — a foot bath ships in
 * exactly the same packaging as a herbal tea ("1 Petit Sachet plante
 * 90g"), so the variant's packaging string alone can't tell them apart
 * and used to mislabel every foot bath as "Tisane". Only when the product
 * itself says nothing (detectProductType returns null) do we fall back to
 * the formats' packaging keywords, and finally to the raw format title — and
 * only when every format agrees: "Petit"/"Grand Sachet plante" are both
 * "Plante", but gélules vs poudre would make any single word a guess (the
 * format field names the chosen one anyway).
 */
function shortFormat(product: Product): string | null {
  const fromProduct = detectProductType(product);
  if (fromProduct) return fromProduct;

  const words = product.variants.map((v) => packagingWord(v.title));
  return words.length > 0 && words.every((w) => w === words[0]) ? words[0] : null;
}

function packagingWord(variantTitle: string | undefined): string | null {
  if (!variantTitle) return null;
  // Type words first, container words last. "Sachet"/"plante" describe what
  // the product is packed in, not what it is — a foot bath and a herbal tea
  // ship in the identical "1 Petit Sachet plante 90g". Reaching this tier at
  // all means the product's own title/families told us nothing, so we report
  // the packaging as-is instead of inferring a use: "plante" is a bag of
  // plants, and calling it "Tisane" would be a guess we have no basis for.
  const t = variantTitle.toLowerCase();
  if (t.includes('gélule') || t.includes('capsule')) return 'Gélules';
  if (t.includes('bain')) return 'Bain';
  if (t.includes('huile') || t.includes('oil')) return 'Huile';
  if (t.includes('crème') || t.includes('cream')) return 'Crème';
  if (t.includes('kit')) return 'Kit';
  if (t.includes('tisane')) return 'Tisane';
  if (t.includes('plante')) return 'Plante';
  if (t.includes('sachet')) return 'Sachet';
  return variantTitle.split(' ').slice(0, 2).join(' ');
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
}
